import { useQuery } from "@tanstack/react-query";
import { getPlanExpiredStatus } from "../../services/userManagement";
import { getUserPlan } from "../../services/credits";
import { userKeys } from "../queryKeys/userKeys";

export const usePlanExpiry = () => {
  return useQuery({
    queryKey: userKeys.planExpiry(),
    queryFn: async () => {
      try {
        const response = await getPlanExpiredStatus();
        if (response?.status === 200) {
          return { ...response.data.data, plan_not_found: false, message: response?.data?.message };
        }
        // Non-200 status (e.g. 400) — plan not found / expiry check failed.
        return { is_plan_expired: true, plan_not_found: true, message: response?.data?.message };
      } catch (error: any) {
        // Axios / network error — try to recover the payload from the response body.
        const errorPayload = error?.response?.data?.data;
        const errorMessage = error?.response?.data?.message;
        if (errorPayload) {
          return { ...errorPayload, plan_not_found: error?.response?.status === 400, message: errorMessage };
        }
        // 400 with no recoverable payload → plan not found.
        if (error?.response?.status === 400) {
          return { is_plan_expired: true, plan_not_found: true, message: errorMessage };
        }
        // For truly unexpected errors (network, 5xx, etc.), fall back to not expired.
        return { is_plan_expired: false, plan_not_found: false, message: undefined };
      }
    },
    staleTime: 1 * 60 * 1000, // cache for 10 minutes
    refetchOnWindowFocus: false,
    select: (data) => ({
      isPlanExpired: data?.is_plan_expired ?? false,
      planNotFound: data?.plan_not_found ?? false,
      message: data?.message,
    }),
  });
};

export const useUserPlan = (hasAccess = true) => {
  return useQuery({
    queryKey: userKeys.userPlan(),
    queryFn: async () => {
      const response = await getUserPlan();
      if (response?.status === 200) {
        return response.data.data;
      }
      throw new Error("Failed to fetch user plan");
    },
    enabled: hasAccess, // ✅ query will NOT run if hasAccess is false
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};